/**
 * HigherAdjunctionsExplainer — adjunctions in higher and graded settings.
 *
 * A 2-adjunction replaces the strict equalities of an ordinary adjunction
 * with 2-cells whose composites satisfy coherence laws. A graded
 * adjunction indexes the unit, counit, and the underlying functors by a
 * grade drawn from a monoid; Kleisli composition combines grades by the
 * monoid product. CSK's Structural Duality Principles all live at this
 * higher level: a graded seam is structurally inexpressible *without* the
 * witness — but expressible *with* it, where ordinary seams are binary.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  GradedAdjunctionDiagram,
  TwoAdjunctionDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-higher-adjunctions",
  kind: "Categorical structure",
  title: "Higher and graded adjunctions",
  subtitle: "2-adjunctions, lax/oplax adjunctions, and grade-indexed F_k ⊣ G_k",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Adjunction", href: "#explainer-adjunction", accent: ACCENT },
  { label: "Triangle identities", href: "#explainer-triangle-identities", accent: ACCENT },
  { label: "Beck-Chevalley", href: "#explainer-beck-chevalley", accent: ACCENT },
  { label: "Graded monad", href: "#explainer-graded-monad" },
  { label: "ALWAYS (SDP)", href: "#explainer-always", accent: ACCENT },
  { label: "REQUIRE (At)", href: "#explainer-comonad", accent: ACCENT },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      Ordinary adjunctions equate two morphisms exactly. <strong>Higher adjunctions</strong> replace
      those equalities with 2-cells; <strong>graded adjunctions</strong> index everything by a grade
      drawn from a monoid. CSK uses higher and graded adjunctions to make seams <em>graded</em>: a
      crossing is unconstructable without the right witness, but constructable with the witness at
      the corresponding grade.
    </p>
  ),
  intuition: (
    <>
      <p>
        Two refinements of the ordinary "<MathInline>{tex`F \dashv G`}</MathInline> on the nose"
        picture appear naturally in CSK.
      </p>
      <p>
        <strong>Going up a dimension.</strong> A 2-adjunction lives between 2-categories — places
        where you have not just objects and arrows but <em>arrows between arrows</em>. The unit and
        counit are no longer equalities; they are 2-cells, and the triangle identities are replaced
        by 2-cell equations called coherence laws. This is the regime that lets CSK talk about
        "almost equal up to a benign rewrite" without abandoning the safety guarantee — the 2-cell
        is the rewrite, and its existence is the proof.
      </p>
      <p>
        <strong>Going up a monoid.</strong> A graded adjunction{" "}
        <MathInline>{tex`F_{k} \dashv G_{k}`}</MathInline> is a family of adjunctions indexed by a
        grade <MathInline>{tex`k`}</MathInline> drawn from a monoid{" "}
        <MathInline>{tex`(K, \cdot, e)`}</MathInline>. Each grade carries its own unit and counit;
        composing two graded Kleisli arrows multiplies their grades. The grade tracks "how much
        governance load did this step incur" — and the monoid product determines whether two steps
        can compose at all.
      </p>
      <p>
        Both refinements answer the same security question more finely:{" "}
        <em>which crossing, which witness, at which grade?</em>
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`\mathcal{C}`}</MathInline>, <MathInline>{tex`\mathcal{D}`}</MathInline>{" "}
        be 2-categories. A <strong>2-adjunction</strong> consists of 2-functors{" "}
        <MathInline>{tex`F : \mathcal{C} \to \mathcal{D}`}</MathInline> and{" "}
        <MathInline>{tex`G : \mathcal{D} \to \mathcal{C}`}</MathInline> with unit and counit{" "}
        <em>2-cells</em>{" "}
        <MathInline>{tex`\eta : \mathrm{Id}_{\mathcal{C}} \Rightarrow G \circ F`}</MathInline> and{" "}
        <MathInline>{tex`\varepsilon : F \circ G \Rightarrow \mathrm{Id}_{\mathcal{D}}`}</MathInline>{" "}
        subject to triangle identities expressed at the 2-cell level (i.e., as equations between
        pasting diagrams of 2-cells).
      </p>
      <TwoAdjunctionDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>Two variants weaken the picture further:</p>
      <ul>
        <li>
          <strong>Lax adjunctions</strong> demand the triangle identities only up to a 2-cell that
          fills in. They model "the build/observe trade is almost two-sided, with an extra coherence
          cell".
        </li>
        <li>
          <strong>Oplax adjunctions</strong> dualize the direction of that filling 2-cell. They
          model "almost two-sided, but the natural fill goes the other way".
        </li>
      </ul>
      <p style={{ marginTop: "0.75rem" }}>
        A <strong>graded adjunction</strong> over a monoid{" "}
        <MathInline>{tex`(K, \cdot, e)`}</MathInline> consists of functors{" "}
        <MathInline>{tex`F_{k} : C \to D`}</MathInline>,{" "}
        <MathInline>{tex`G_{k} : D \to C`}</MathInline> for each grade{" "}
        <MathInline>{tex`k \in K`}</MathInline>, together with grade-indexed natural
        transformations:
      </p>
      <GradedAdjunctionDiagram accent={ACCENT} grade="k" />
      <ul>
        <li>
          <strong>Unit</strong>{" "}
          <MathInline>{tex`\eta_{k} : \mathrm{Id} \Rightarrow G_{k} \circ F_{k}`}</MathInline>
        </li>
        <li>
          <strong>Counit</strong>{" "}
          <MathInline>{tex`\varepsilon_{k} : F_{k} \circ G_{k} \Rightarrow \mathrm{Id}`}</MathInline>
        </li>
      </ul>
      <p>subject to:</p>
      <ul>
        <li>
          <strong>Grade identity:</strong> <MathInline>{tex`F_{e} \dashv G_{e}`}</MathInline> is the
          ordinary identity adjunction.
        </li>
        <li>
          <strong>Grade composition:</strong> for grades <MathInline>{tex`j, k \in K`}</MathInline>,{" "}
          <MathInline>{tex`F_{j \cdot k}`}</MathInline> factors through{" "}
          <MathInline>{tex`F_{j} \circ F_{k}`}</MathInline> up to a coherence 2-cell.
        </li>
        <li>
          <strong>Triangle identities</strong> at every grade.
        </li>
      </ul>
      <p>
        Higher and graded adjunctions specialize to the ordinary case when the 2-cells are
        identities and when <MathInline>{tex`K`}</MathInline> is the trivial monoid.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Every CSK <strong>Structural Duality Principle</strong> (Tr, Al, St, At, Dt, De, Pa) is an
        adjunction at the 2-categorical or graded level. The four forbidden generators (G1–G4)
        become graded: each is unconstructable without a witness, expressible with the witness at
        the corresponding grade.
      </p>
      <ul>
        <li>
          <code>TRACE</code> (Tr) — cartesian reindex <MathInline>{tex`\dashv`}</MathInline>{" "}
          opcartesian pushforward, both indexed by the fibration's base. The grade is the reindexing
          label; a missing label is a missing unit at that grade.
        </li>
        <li>
          <code>ALWAYS</code> (Al) — free F-algebra <MathInline>{tex`\dashv`}</MathInline> cofree
          F-coalgebra. The grade tracks invariant strength; a stronger invariant requires a stronger
          witness.
        </li>
        <li>
          <code>REQUIRE</code> (At) — commitment tracking <MathInline>{tex`\dashv`}</MathInline>{" "}
          requirement discharging. The grade is the precommitment payload; composing two
          precommitments multiplies their grades by the monoid product.
        </li>
      </ul>
      <p>
        Practically: a graded seam answers "yes, this crossing exists — but only at this grade and
        with this witness". A 2-adjunction lets that witness be a rewrite (the 2-cell) rather than a
        bare term — exactly what an auditor wants when reviewing a benign refactor that should not
        weaken the safety story.
      </p>
      <p>
        Graded and higher adjunctions are also why ICGs (Immunity Closure Guarantees) can stack: the
        unit at grade <MathInline>{tex`j`}</MathInline> composes with the counit at grade{" "}
        <MathInline>{tex`k`}</MathInline> through the monoid product, and the result is the closure
        witness at grade <MathInline>{tex`j \cdot k`}</MathInline>. No "magic"; just the algebraic
        content of the grading.
      </p>
    </>
  ),
  references: [
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Graded crossing model for the seven SDPs.",
    },
    {
      label: "Two-categorical adjunctions (Agda)",
      path: "agda/Foundation/TwoCatAdjunctions/BeckMonadicity.agda",
      note: "Mechanized 2-adjunctions including the BC monadicity witness.",
    },
    {
      label: "GradedMonad (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/GradedMonad.agda",
      note: "Grade-indexed Kleisli composition; the algebraic basis for graded PRECOMMIT.",
    },
    {
      label: "Higher-category sugar (Agda)",
      path: "agda/Foundation/Syntax/HigherCategorySugar.agda",
      note: "Surface syntax for 2-cells and coherence equations used across CSK.",
    },
    {
      label: "PRECOMMIT/GradedMonad.agda",
      path: "agda/KernelAdjacent/PRECOMMIT/GradedMonad.agda",
      note: "Production realization of the graded-monad pattern.",
    },
  ],
};

export function HigherAdjunctionsExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<TwoAdjunctionDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
